import {
	formatVariableValue,
	NODE_TYPE_LABELS,
	type NodeType,
	type ReplyButton,
	type SessionStatus,
} from "@velizhanin-dev/bot-scenario"
import { Braces, FastForward, Hourglass, RotateCcw, SendHorizontal, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import type { ScenarioDraft } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { TG, WALLPAPER_STYLE } from "../lib/telegram-theme"
import { usePauseClock } from "../model/use-pause-clock"
import type { TestChat } from "../model/use-test-chat"
import { AttachMenu, CommandsMenu, DEFAULT_TEST_LOCATION, DEFAULT_TEST_PHONE } from "./composer"
import { Entry } from "./entry"
import { currentReplyKeyboard, ReplyKeyboardView, TypingBubble } from "./reply-keyboard"

/** Так движок пишет в журнал «бот печатает»; в чате это пузырь с точками, а не строка. */
const TYPING_TEXT = "Бот печатает…"

function formatPause(ms: number): string {
	const total = Math.ceil(ms / 1000)
	if (total < 3600) return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
	return new Date(Date.now() + ms).toLocaleString("ru-RU", {
		day: "numeric",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	})
}

/** Подпись под именем бота в шапке, как статус собеседника в Telegram. */
const STATUS_LABELS: Record<SessionStatus, string> = {
	IDLE: "сценарий закончился",
	WAITING_INPUT: "ждёт ответ",
	WAITING_BUTTON: "ждёт нажатия кнопки",
	WAITING_DELAY: "на паузе",
	WAITING_REQUEST: "выполняет запрос",
	WAITING_PAYMENT: "ждёт оплату",
}

interface TestChatPanelProps {
	draft: ScenarioDraft
	chat: TestChat
	onClose: () => void
	onShowNode: (nodeId: string) => void
}

/** Тест-чат по живому черновику: те же ответы, что даст бот, но без Telegram. */
export function TestChatPanel({ draft, chat, onClose, onShowNode }: TestChatPanelProps) {
	const [tab, setTab] = useState<"chat" | "vars">("chat")
	const [text, setText] = useState("")
	const scrollRef = useRef<HTMLDivElement>(null)
	const { state, isBusy } = chat
	const transcript = state?.transcript ?? []
	const session = state?.session ?? null
	const replyKeyboard = currentReplyKeyboard(transcript)
	const waitingNode = session?.waiting
		? draft.nodes.find((node) => node.id === session.waiting?.nodeId)
		: undefined

	const bot = draft.bots[0]
	const botName = bot?.name ?? "Тестовый бот"
	const commands = [
		...new Map(
			draft.flows
				.flatMap((flow) => flow.triggers?.commands ?? [])
				.map((item) => [item.command, item] as const),
		).values(),
	]

	const pause = usePauseClock(state?.pendingWakeup, isBusy, () =>
		void chat.send({ type: "wakeup", elapsed: true }),
	)
	const lastEntry = transcript.at(-1)
	// Бот «печатает» перед сообщением: пока идёт эта пауза, показываем точки, как в Telegram.
	const typingPause =
		!!pause &&
		session?.status === "WAITING_DELAY" &&
		lastEntry?.from === "system" &&
		lastEntry.text === TYPING_TEXT
	const visibleTranscript = transcript.filter(
		(entry) => !(entry.from === "system" && entry.text === TYPING_TEXT),
	)

	const lastEntryId = lastEntry?.id
	// Новое сообщение, «печатает…» или возврат на вкладку чата: прокрутка к последнему.
	// biome-ignore lint/correctness/useExhaustiveDependencies: lastEntryId и isBusy: поводы прокрутить, а не данные эффекта
	useEffect(() => {
		const element = scrollRef.current
		if (element && tab === "chat") element.scrollTop = element.scrollHeight
	}, [lastEntryId, isBusy, typingPause, tab])

	const sendText = async () => {
		const value = text.trim()
		if (!value || isBusy) return
		setText("")
		await chat.send({ type: "text", text: value })
	}

	const pressReply = (button: ReplyButton) => {
		if (button.kind === "web_app") window.open(button.url, "_blank", "noopener")
		else if (button.kind === "contact") void chat.send({ type: "contact", phone: DEFAULT_TEST_PHONE })
		else if (button.kind === "location")
			void chat.send({ type: "location", ...DEFAULT_TEST_LOCATION })
		else void chat.send({ type: "text", text: button.label })
	}

	const vars = Object.entries(session?.vars ?? {}).sort(([a], [b]) => a.localeCompare(b))
	const status =
		isBusy || typingPause
		? "печатает…"
		: session
			? STATUS_LABELS[session.status]
			: "тест по черновику, без публикации"

	return (
		<div className="flex h-full flex-col text-white" style={{ backgroundColor: TG.wallpaper }}>
			<div
				className="flex items-center gap-3 border-b border-black/30 px-3 py-2.5"
				style={{ backgroundColor: TG.panel }}
			>
				<div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-400 to-blue-600 text-base font-semibold">
					{botName.trim().charAt(0).toUpperCase() || "Б"}
				</div>
				<div className="min-w-0 flex-1">
					<p className="truncate text-[15px] leading-5 font-semibold">{botName}</p>
					<Button
						type="button"
						variant="ghost"
						disabled={!waitingNode}
						title={waitingNode ? "Показать блок, который ждёт" : undefined}
						onClick={() => waitingNode && onShowNode(waitingNode.id)}
						className={cn(
							"h-auto max-w-full justify-start truncate p-0 text-left text-xs leading-4 hover:bg-transparent disabled:opacity-100",
							isBusy ? "text-sky-300" : "text-white/50",
							waitingNode && "hover:text-sky-300",
						)}
					>
						{status}
						{waitingNode && !isBusy
							? ` · ${NODE_TYPE_LABELS[waitingNode.type as NodeType] ?? waitingNode.type}`
							: ""}
					</Button>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					aria-pressed={tab === "vars"}
					aria-label="Переменные сессии"
					title="Переменные сессии"
					onClick={() => setTab((current) => (current === "vars" ? "chat" : "vars"))}
					className={cn(
						"relative h-9 w-9 rounded-full text-white/60 hover:bg-white/5 hover:text-white",
						tab === "vars" && "bg-white/10 text-white",
					)}
				>
					<Braces className="h-[18px] w-[18px]" />
					{vars.length > 0 ? (
						<span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-sky-500 px-1 text-[10px] leading-4 font-semibold">
							{vars.length}
						</span>
					) : null}
				</Button>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					aria-label="Сбросить тест-чат"
					title="Начать заново"
					disabled={isBusy}
					onClick={() => void chat.reset()}
					className="h-9 w-9 rounded-full text-white/60 hover:bg-white/5 hover:text-white disabled:opacity-40"
				>
					<RotateCcw className="h-[18px] w-[18px]" />
				</Button>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					aria-label="Закрыть тест-чат"
					onClick={onClose}
					className="h-9 w-9 rounded-full text-white/60 hover:bg-white/5 hover:text-white"
				>
					<X className="h-[18px] w-[18px]" />
				</Button>
			</div>

			{tab === "chat" ? (
				<div className="relative min-h-0 flex-1" style={WALLPAPER_STYLE}>
					<div ref={scrollRef} className="h-full space-y-2 overflow-y-auto px-3 pt-3 pb-12">
						{chat.isLoading ? (
							<p className="py-10 text-center text-sm text-white/50">Загрузка…</p>
						) : null}
						{!chat.isLoading && transcript.length === 0 && !isBusy ? (
							<div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
								<div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-sky-400 to-blue-600 text-2xl font-semibold">
									{botName.trim().charAt(0).toUpperCase() || "Б"}
								</div>
								<div className="space-y-1.5">
									<p className="font-semibold">Проверьте сценарий до публикации</p>
									<p className="text-sm leading-snug text-white/55">
										Чат идёт по черновику. HTTP-запросы выполняются по-настоящему, а уведомления и
										оплата остаются в этом окне.
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									onClick={() => void chat.send({ type: "start" })}
									className="h-auto rounded-full bg-[#2b5278] px-5 py-2 text-sm font-medium text-white hover:bg-[#346299] hover:text-white"
								>
									Запустить /start
								</Button>
								<p className="text-xs text-white/40">
									или выберите блок и нажмите «Проиграть отсюда»
								</p>
							</div>
						) : null}
						{visibleTranscript.map((entry) => (
							<Entry
								key={entry.id}
								entry={entry}
								scenarioId={draft.id}
								disabled={isBusy}
								onPress={(callbackData) => void chat.send({ type: "button", callbackData })}
								onPay={(nodeId) => void chat.send({ type: "payment", nodeId })}
								onShowNode={onShowNode}
							/>
						))}
						{isBusy || typingPause ? <TypingBubble /> : null}
					</div>
					{pause && !typingPause ? (
						<div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
							<div className="pointer-events-auto flex items-center gap-1 rounded-full bg-black/55 py-1 pr-1 pl-3.5 text-xs font-medium text-white shadow-lg backdrop-blur">
								<Hourglass className="size-3.5 text-white/70" />
								<span className="tabular-nums">
									{pause.auto ? `Пауза ${formatPause(pause.remainingMs)}` : `Пауза до ${formatPause(pause.remainingMs)}`}
								</span>
								<Button
									type="button"
									variant="ghost"
									disabled={isBusy}
									onClick={() => void chat.send({ type: "wakeup" })}
									className="h-7 gap-1 rounded-full px-2.5 text-xs text-white hover:bg-white/15 hover:text-white"
								>
									<FastForward className="size-3.5" />
									Пропустить
								</Button>
							</div>
						</div>
					) : null}
				</div>
			) : (
				<div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm">
					<p className="mb-2 text-[11px] font-medium tracking-wide text-white/40 uppercase">
						Переменные сессии
					</p>
					{vars.length === 0 ? (
						<p className="text-white/50">Пока пусто. Появятся по ходу диалога.</p>
					) : (
						<dl className="divide-y divide-white/5">
							{vars.map(([name, value]) => (
								<div key={name} className="grid grid-cols-[auto_1fr] gap-3 py-2">
									<dt className="font-mono text-xs leading-5 text-sky-300/80">{name}</dt>
									<dd className="text-right break-words text-white/90">
										{formatVariableValue(value)}
									</dd>
								</div>
							))}
						</dl>
					)}
				</div>
			)}

			<div className="border-t border-black/30 px-1.5 py-1.5" style={{ backgroundColor: TG.panel }}>
				<form
					className="flex items-center gap-0.5"
					onSubmit={(event) => {
						event.preventDefault()
						void sendText()
					}}
				>
					<CommandsMenu
						commands={commands}
						disabled={isBusy}
						onStart={(payload) => void chat.send({ type: "start", payload })}
						onCommand={(command) => void chat.send({ type: "text", text: `/${command}` })}
					/>
					<AttachMenu disabled={isBusy} onSend={(event) => void chat.send(event)} />
					<input
						aria-label="Сообщение боту"
						placeholder="Сообщение"
						className={cn(
							"h-10 min-w-0 flex-1 rounded-full px-4 text-base text-white outline-none placeholder:text-white/40 focus:ring-1 focus:ring-sky-500/40 md:text-sm",
							TG.field,
						)}
						value={text}
						onChange={(event) => setText(event.target.value)}
					/>
					<Button
						type="submit"
						variant="ghost"
						size="icon"
						aria-label="Отправить"
						disabled={!text.trim() || isBusy}
						className="ml-1 h-10 w-10 shrink-0 rounded-full bg-[#2b5278] text-white hover:bg-[#346299] hover:text-white disabled:bg-transparent disabled:text-white/30 disabled:opacity-100"
					>
						<SendHorizontal className="h-5 w-5" />
					</Button>
				</form>
			</div>
			{replyKeyboard ? (
				<ReplyKeyboardView keyboard={replyKeyboard} disabled={isBusy} onPress={pressReply} />
			) : null}
		</div>
	)
}
